<!--
  ~ This code is protected by intellectual property rights.
  ~ Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
  ~ © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
  -->

@if (errorMessage$ | async; as msg) {
  <pidx-inline-form-messages [messages]="[msg | translate]"></pidx-inline-form-messages>
}

<profile-pds-p-text p-text id="required-fields-hint">{{ 'pidaw.generic.form.requiredFields.hint' | translate }}</profile-pds-p-text>

<form
  [formGroup]="formGroup"
  (ngSubmit)="submitForm(formGroup)"
  myprofileUiValidationTrack="phone_editform"
  id="form"
  class="profile:mt-fluid-md"
  [attr.aria-label]="'pidpaw.editPhone.form.aria' | translate"
  aria-describedby="required-fields-hint"
>
  <div>
    @if (!editedPhone || !editedPhone.isPorscheId) {
      <div>
        @if (!editedPhone) {
          <div role="radiogroup" [attr.aria-label]="'pidpaw.editPhone.radio.phoneType.groupAria' | translate">
            <profile-pds-p-radio-group p-radio-group formControlName="phoneType" direction="row" (change)="selectType($event)">
              <profile-pds-p-radio-group-option
                p-radio-group-option
                class="fs-exclude"
                data-protractor-id="radio-mobile"
                [label]="'pidpaw.editPhone.radio.phoneTypeMobile.label' | translate"
                [value]="'mobile'"
                [myprofileUiTrack]="{
                  clickElementType: 'radio',
                  clickElementId: 'phonenumber',
                  clickElementName: 'change_mobile',
                }"
              ></profile-pds-p-radio-group-option>
              <profile-pds-p-radio-group-option
                p-radio-group-option
                class="fs-exclude"
                data-protractor-id="radio-landline"
                [label]="'pidpaw.editPhone.radio.phoneTypeLandline.label' | translate"
                [value]="'landline'"
                [myprofileUiTrack]="{
                  clickElementType: 'radio',
                  clickElementId: 'phonenumber',
                  clickElementName: 'change_landline',
                }"
              ></profile-pds-p-radio-group-option>
            </profile-pds-p-radio-group>
          </div>
        }
        @if (editedPhone) {
          <div class="profile:inline-flex">
            @if (formGroup.get('phoneType')!.value === 'mobile') {
              <profile-pds-p-icon p-icon name="mobile" aria-hidden="true" color="contrast-medium" size="xx-small"></profile-pds-p-icon>
            }
            @if (formGroup.get('phoneType')!.value === 'landline') {
              <profile-pds-p-icon p-icon name="phone" aria-hidden="true" color="contrast-medium" size="xx-small"></profile-pds-p-icon>
            }
            <profile-pds-p-text
              p-text
              size="inherit"
              class="profile:text-[14px] profile:ms-static-xs profile:inline-block"
              color="contrast-medium"
              >{{
                formGroup.get('phoneType')!.value === 'mobile'
                  ? ('pidpaw.editPhone.icon.phoneTypeMobile.label' | translate)
                  : ('pidpaw.editPhone.icon.phoneTypeLandline.label' | translate)
              }}</profile-pds-p-text
            >
          </div>
        }
        <div class="profile:mt-fluid-md">
          <profile-pds-p-input-tel
            p-input-tel
            [state]="!!phoneNumberFormControlErrorMessage ? 'error' : 'none'"
            [message]="phoneNumberFormControlErrorMessage"
            id="phoneNumberInput"
            data-protractor-id="phone-number-input"
            class="fs-exclude"
            name="number"
            formControlName="number"
            [readOnly]="!!(this.editedPhone?.isVerified || (this.editedPhone && this.editedPhone.isStandard))"
            [placeholder]="'pidpaw.editPhone.input.phone.placeholder' | translate"
            [attr.aria-label]="'pidpaw.editPhone.input.phone.label' | translate"
          >
          </profile-pds-p-input-tel>
        </div>
      </div>
    }

    @if (this.formGroup.get('isVerified')!.value === true) {
      <profile-pds-p-text class="profile:text-[14px] profile:mt-static-sm" p-text size="inherit" color="contrast-medium">
        <div [innerHTML]="'pidp.editPhone.verifiedMobile.description.label' | translate"></div>
      </profile-pds-p-text>
    }

    @if (formGroup.get('phoneType')!.value === 'mobile' || phones.length === 0) {
      <profile-pds-p-checkbox
        p-checkbox
        class="profile:mt-fluid-md"
        [label]="'pidpaw.editPhone.checkbox.defaultPhone.label' | translate"
        [hideLabel]="false"
        data-protractor-id="checkbox-is-standard"
        name="isStandard"
        formControlName="isStandard"
      >
      </profile-pds-p-checkbox>
    }

    @if (phoneIdBeingEdited && !editedPhone?.isPorscheId) {
      <profile-pds-p-button-pure
        [aria]="{
          'aria-label': ('pidpaw.selectPhone.button.deletePhone.labelA11y' | translate: { PHONE_NUMBER: deleteButtonPhoneNumberForAria }),
          'aria-haspopup': 'dialog',
        }"
        p-button-pure
        data-protractor-id="phone-widget-editor-delete-phone-number-button"
        icon="delete"
        (click)="deleteThisPhone($event)"
        class="profile:mt-fluid-sm profile:py-fluid-sm"
        [myprofileUiTrack]="{
          clickElementType: 'button',
          clickElementId: 'phonenumber',
          clickElementName: 'delete',
        }"
        >{{ 'pidpaw.generic.button.delete.label' | translate }}</profile-pds-p-button-pure
      >
    }
  </div>

  @if (!(isBlankCheckoutModeEditor$ | async)) {
    <div class="profile:flex profile:flex-col profile:gap-y-static-sm profile:sm:gap-x-fluid-sm profile:sm:flex-row profile:mt-fluid-md">
      <profile-pds-p-button
        [aria]="{
          'aria-label':
            isSaveAndVerify && isUSCountry
              ? ('pidpaw.editPhone.button.saveAndVerify.aria' | translate)
              : ('pidpaw.editPhone.button.save.aria' | translate),
        }"
        p-button
        variant="primary"
        type="submit"
        icon="check"
        data-protractor-id="phone-widget-editor-save-phone-number-button"
        [myprofileUiTrack]="{
          clickElementType: 'button',
          clickElementId: 'phonenumber',
          clickElementName: 'save',
        }"
      >
        {{
          (isSaveAndVerify && isUSCountry ? 'pidp.personalData.phoneNumbers.saveAndVerify.button' : 'pidpaw.editPhone.button.save.label')
            | translate
        }}
      </profile-pds-p-button>
      <profile-pds-p-button
        [aria]="{ 'aria-label': ('pidpaw.editPhone.button.cancel.aria' | translate) }"
        p-button
        variant="secondary"
        (click)="cancel()"
        icon="close"
        data-protractor-id="phone-widget-editor-cancel-button"
        [myprofileUiTrack]="{
          clickElementType: 'button',
          clickElementId: 'phonenumber',
          clickElementName: 'cancel',
        }"
      >
        {{ 'pidpaw.editPhone.button.cancel.label' | translate }}
      </profile-pds-p-button>
    </div>
  }

  @if (shouldShowVerificationDescription() && this.hasConfirmedVehicle) {
    <profile-pds-p-text p-text size="inherit" class="profile:mt-fluid-md profile:text-[14px] profile:inline-block">
      {{ 'pidp.mobile.verification.description' | translate }}
    </profile-pds-p-text>
  }
</form>

@if (editorIsLoading$ | async) {
  <pidx-loader [mode]="PidxLoaderModeEnum.OVERLAY" [text]="editorLoadingLabel"></pidx-loader>
}
